import { SettingsSearchNotFound } from 'App/Map/UserSettingsMenu/components/SettingsSearchNotFound/SettingsSearchNotFound'
import { IconContact } from 'components/icons/IconContact'
import { translate } from 'i18n/i18n'
import type { Dispatch, SetStateAction } from 'react'
import { apiFetch } from 'utils/apiFetch'
import { showNotification } from 'utils/showNotification'
import { useIsWideScreen } from 'utils/useIsWideScreen'
import { showConfirmSettingModal } from '../../_utils/showConfirmSettingModal'
import type { NotificationType } from '../_utils/useRelativeNotification'
import { ContactsAndGroupsRow } from '../components/ContactsAndGroupsRow'
import { useSearchGroupsAndContacts } from '../components/useSearchGroupsAndContacts'
import type { Contact } from '../ContactsAndGroupsContext'
import { useGroupsAndContacts } from '../ContactsAndGroupsContext'

export const Contacts = ({
  onEdit,
  setSelectedContact,
  showRelativeNotification,
}: {
  onEdit: () => void
  setSelectedContact: Dispatch<SetStateAction<Contact | null>>
  showRelativeNotification: ({ type, message }: { type: NotificationType; message: string }) => void
}) => {
  const isWideScreen = useIsWideScreen()
  const notificationFunctionToUse = isWideScreen ? showRelativeNotification : showNotification
  const { contacts: allContacts, deleteContact } = useGroupsAndContacts()

  const { SearchInputComponent, filteredAndSortedList, isSearchMatch } = useSearchGroupsAndContacts({
    initialList: allContacts,
    placeholder: translate.phrases.banyanApp('Search Contacts'),
  })

  const onDelete = (contactId: string, contactName: string) => {
    showConfirmSettingModal({
      title: translate.phrases.banyanApp('Delete Contact', { contactName }),
      content: (
        <div>
          <p>
            {translate.phrases.banyanApp('Are you sure you want to delete {{contactName}} as a contact?', {
              contactName,
            })}
          </p>
        </div>
      ),
      onConfirm: () => handleDeleteContact(contactId),
      confirmButtonText: translate.phrases.banyanApp('Delete Contact'),
    })
  }

  const handleDeleteContact = async (contactId: string) => {
    try {
      await apiFetch({
        url: '/contacts-management',
        body: {
          contactsToDelete: [contactId],
        },
      })

      notificationFunctionToUse({
        type: 'success',
        message: translate.phrases.banyanApp('Contact successfully deleted'),
      })

      deleteContact(contactId)
    } catch (error) {
      notificationFunctionToUse({
        type: 'error',
        message: translate.phrases.banyanApp('Failed to delete contact'),
      })
    }
  }

  return (
    <>
      {SearchInputComponent}
      {isSearchMatch ? (
        (filteredAndSortedList as Contact[]).map((contact) => {
          return (
            <ContactsAndGroupsRow
              id={contact.id}
              key={contact.id}
              icon={<IconContact />}
              name={contact.name}
              onEdit={() => {
                onEdit()

                setSelectedContact(contact)
              }}
              onDelete={onDelete}
            />
          )
        })
      ) : (
        <SettingsSearchNotFound />
      )}
    </>
  )
}
